import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Receipt, Zap, FileDown, Check, Undo2, Trash2, Square, CheckSquare, Eye, Plus, X, FolderDown, Mail, BellRing, Upload } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_TONE = {
  concept: "bg-neutral-100 text-neutral-700 border-neutral-200",
  verzonden: "bg-blue-50 text-blue-700 border-blue-200",
  betaald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  overtijd: "bg-red-50 text-red-700 border-red-200",
  gecrediteerd: "bg-amber-50 text-amber-700 border-amber-200",
};

const MAANDEN = ["Jan", "Feb", "Mrt", "Apr", "Mei", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

export default function FactureringPage() {
  const { user } = useAuth();
  const canWrite = can(user, "invoices", "write");
  const canDelete = can(user, "invoices", "delete");
  const [rows, setRows] = useState([]);
  const [kinderen, setKinderen] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");
  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [selected, setSelected] = useState(() => new Set());
  const [detailInvoice, setDetailInvoice] = useState(null);
  const [locations, setLocations] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ location_id: "", year: new Date().getFullYear() });
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = useCallback(async () => {
    const params = { year };
    if (statusFilter !== "all") params.status = statusFilter;
    const [i, k, l] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/children"),
      api.get("/locations"),
    ]);
    setRows(i.data); setKinderen(k.data); setLocations(l.data);
    setSelected(new Set());
  }, [year, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, s) => {
    try { await api.post(`/invoices/${id}/status`, { status: s }); toast.success(`Status bijgewerkt naar ${s}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const generate = async () => {
    try {
      const r = await api.post("/invoices/generate", { year: parseInt(genForm.year), month: parseInt(genForm.month) });
      toast.success(`${r.data.created} facturen gegenereerd`);
      setGenOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkEmail = async () => {
    if (!window.confirm(`Facturen van ${genForm.month}/${genForm.year} verzenden per e-mail?`)) return;
    try {
      const r = await api.post("/invoices/bulk-email",
        { year: parseInt(genForm.year), month: parseInt(genForm.month) });
      const sk = r.data.skipped?.length || 0;
      toast.success(`${r.data.sent} verzonden${sk ? ` · ${sk} overgeslagen (geen e-mail)` : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Bulk-email mislukt"); }
  };

  const runReminders = async () => {
    if (!window.confirm("Betalingsherinneringen nu versturen voor alle openstaande facturen (14/21/30 dagen regel)?")) return;
    try {
      const r = await api.post("/invoices/reminders/run", {});
      const count = r.data.count || 0;
      if (count === 0) toast.info("Geen facturen die momenteel een herinnering nodig hebben");
      else toast.success(`${count} herinneringen verstuurd`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Herinneringen mislukt"); }
  };

  const downloadBulk = async () => {
    if (!bulkForm.location_id) { toast.error("Kies eerst een locatie"); return; }
    setBulkLoading(true);
    try {
      const res = await api.get(
        `/locations/${bulkForm.location_id}/jaaropgaves/${bulkForm.year}/zip`,
        { responseType: "blob" }
      );
      const count = res.headers?.["x-jaaropgaves-count"];
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      const loc = locations.find(l => l.id === bulkForm.location_id);
      const safeLoc = (loc?.name || "locatie").replace(/[^a-z0-9]/gi, "_");
      a.href = url; a.download = `Jaaropgaves_${bulkForm.year}_${safeLoc}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${count || "?"} jaaropgaves gedownload`);
      setBulkOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk-download mislukt");
    }
    setBulkLoading(false);
  };

  const toggleSel = (id) => {
    setSelected(prev => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const toggleAll = () => {
    setSelected(prev => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map(r => r.id));
    });
  };
  const bulkMarkPaid = async () => {
    if (!selected.size) return;
    if (!window.confirm(`${selected.size} factu(u)r(en) markeren als BETAALD?`)) return;
    try {
      const r = await api.post("/invoices/bulk-status", { ids: [...selected], status: "betaald" });
      toast.success(`${r.data.updated} facturen bijgewerkt`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    const reason = window.prompt(`Reden voor bulk-verwijdering van ${selected.size} factu(u)r(en) (audit-log):`, "");
    if (reason === null) return;
    if (!window.confirm(`${selected.size} factu(u)r(en) definitief verwijderen? Kan niet ongedaan worden gemaakt.`)) return;
    try {
      const r = await api.post("/invoices/bulk-delete", { ids: [...selected], reason: reason || "" });
      toast.success(`${r.data.deleted} facturen verwijderd`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const kindMap = Object.fromEntries(kinderen.map(k => [k.id, k]));

  const totals = rows.reduce((acc, r) => {
    acc.total += r.total;
    acc[r.status] = (acc[r.status] || 0) + r.total;
    return acc;
  }, { total: 0 });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0;

  const columns = [
    { key: "_sel", label: "", sortable: false, render: r => (
      <button
        onClick={(e) => { e.stopPropagation(); toggleSel(r.id); }}
        data-testid={`sel-invoice-${r.id}`}
        className="text-neutral-500 hover:text-[color:var(--sage)]"
        aria-label={selected.has(r.id) ? "Deselecteer" : "Selecteer"}
      >
        {selected.has(r.id)
          ? <CheckSquare className="h-4 w-4 text-[color:var(--sage)]" />
          : <Square className="h-4 w-4" />}
      </button>
    )},
    { key: "invoice_number", label: "Factuurnr.", render: r => <span className="font-mono text-xs">{r.invoice_number}</span> },
    { key: "period_month", label: "Periode", render: r => `${MAANDEN[r.period_month - 1]} ${r.period_year}` },
    { key: "kind_id", label: "Kind", render: r => {
      const k = kindMap[r.kind_id];
      return k ? `${k.voornaam} ${k.achternaam}` : "—";
    }},
    { key: "total", label: "Bedrag", render: r => <span className="font-mono">€ {r.total.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</span> },
    { key: "status", label: "Status", render: r => <Badge variant="outline" className={`border ${STATUS_TONE[r.status]}`}>{r.status}</Badge> },
    { key: "actions", label: "", sortable: false, render: r => (
      <div className="flex items-center gap-1 flex-wrap">
        <Button size="sm" variant="ghost" data-testid={`detail-invoice-${r.id}`}
          onClick={(e) => { e.stopPropagation(); setDetailInvoice(r); }}
          title="Bekijk factuurdetails, regels, LRK">
          <Eye className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" data-testid={`ubl-${r.id}`}
          onClick={async (e) => {
            e.stopPropagation();
            try {
              const res = await api.get(`/invoices/${r.id}/ubl`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
              const a = document.createElement("a");
              a.href = url; a.download = `${r.invoice_number}.ubl.xml`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("UBL-XML gedownload");
            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          }}
          title="UBL 2.1 export voor boekhouding">
          <FileDown className="h-3 w-3" />
        </Button>
        {canWrite && r.status !== "betaald" && (
          <Button size="sm" variant="outline"
            data-testid={`mark-paid-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setStatus(r.id, "betaald"); }}
            className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs"
            title="Markeer deze factuur als betaald">
            <Check className="h-3 w-3 mr-1" /> Betaald
          </Button>
        )}
        {canWrite && r.status === "betaald" && (
          <Button size="sm" variant="outline"
            data-testid={`unmark-paid-${r.id}`}
            onClick={(e) => { e.stopPropagation(); setStatus(r.id, "verzonden"); }}
            className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50 text-xs"
            title="Betaalstatus terugdraaien">
            <Undo2 className="h-3 w-3 mr-1" /> Niet betaald
          </Button>
        )}
        {canWrite && (
          <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`status-select-${r.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {["concept", "verzonden", "betaald", "overtijd", "gecrediteerd"].map(s =>
                <SelectItem key={s} value={s}>{s}</SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
        {canDelete && (
          <Button size="sm" variant="ghost"
            data-testid={`delete-invoice-${r.id}`}
            onClick={async (e) => {
              e.stopPropagation();
              const reason = window.prompt("Reden voor verwijdering (audit-log):", "");
              if (reason === null) return;
              if (!window.confirm(`Factuur ${r.invoice_number} definitief verwijderen? Dit is niet ongedaan te maken.`)) return;
              try {
                await api.delete(`/invoices/${r.id}`, { params: { reason: reason || "" } });
                toast.success("Factuur verwijderd");
                load();
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}
            className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            title="Superadmin: verwijder factuur (audit-gelogd)">
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    )},
  ];

  return (
    <div>
      <PageHeader
        title="Facturering"
        subtitle="Alle facturen. Genereer maandelijks conceptfacturen op basis van actieve contracten."
        testid="facturering-header"
        actions={
          <div className="flex items-center gap-2">
            <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {[year + 1, year, year - 1, year - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32 sm:w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Alle statussen</SelectItem>
                {["concept", "verzonden", "betaald", "overtijd", "gecrediteerd"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {canWrite && (
              <Button variant="outline" onClick={runReminders} data-testid="run-reminders-btn"
                      className="border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0"
                      title="Verstuur betalingsherinneringen (14/21/30 dagen)">
                <BellRing className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Herinneringen</span>
              </Button>
            )}
            {canWrite && (
              <Button variant="outline" onClick={bulkEmail} data-testid="bulk-email-btn"
                      className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage)]/5 shrink-0"
                      title={`Verzend alle concept-facturen van ${genForm.month}/${genForm.year} per e-mail`}>
                <Mail className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Verzend </span>e-mail
              </Button>
            )}
            {canWrite && (
              <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="bulk-jaaropgaves-btn"
                          className="border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage)]/5 shrink-0"
                          title="Download ZIP met jaaropgaves per locatie">
                    <FolderDown className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Jaaropgaves </span>ZIP
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Bulk-jaaropgaves downloaden</DialogTitle>
                    <DialogDescription>
                      ZIP-bundel met jaaropgave-PDF per kind voor één locatie. Handig
                      voor de belastingaangifte kinderopvangtoeslag.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Locatie</Label>
                      <Select value={bulkForm.location_id}
                              onValueChange={v => setBulkForm({ ...bulkForm, location_id: v })}>
                        <SelectTrigger data-testid="bulk-location"><SelectValue placeholder="Kies locatie" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Jaar</Label>
                      <Input type="number" value={bulkForm.year}
                             onChange={e => setBulkForm({ ...bulkForm, year: parseInt(e.target.value) || 0 })}
                             data-testid="bulk-year" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setBulkOpen(false)}>Annuleren</Button>
                    <Button onClick={downloadBulk} disabled={bulkLoading}
                            data-testid="bulk-submit"
                            style={{ background: "var(--sage)" }} className="text-white">
                      <FolderDown className="h-4 w-4 mr-1" />
                      {bulkLoading ? "Bundelen…" : "Download ZIP"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {canWrite && (
              <Dialog open={genOpen} onOpenChange={setGenOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="generate-invoices-btn" style={{ background: "var(--sage)" }} className="text-white shrink-0">
                    <Zap className="h-4 w-4 mr-1" /> <span className="hidden sm:inline">Facturen </span>Genereren
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Facturen genereren</DialogTitle>
                    <DialogDescription>Maak conceptfacturen aan voor alle actieve contracten. Bestaande facturen worden overgeslagen.</DialogDescription>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Jaar</Label><Input type="number" value={genForm.year} onChange={e => setGenForm({ ...genForm, year: e.target.value })} /></div>
                    <div>
                      <Label>Maand</Label>
                      <Select value={String(genForm.month)} onValueChange={v => setGenForm({ ...genForm, month: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {MAANDEN.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setGenOpen(false)}>Annuleren</Button>
                    <Button onClick={generate} data-testid="generate-submit" style={{ background: "var(--sage)" }} className="text-white">Genereer</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {["concept", "verzonden", "betaald"].map(s => (
          <div key={s} className="bg-white rounded-lg border border-neutral-200 p-5" data-testid={`total-${s}`}>
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-neutral-400" />
              <div className="text-xs text-neutral-500 uppercase tracking-wider">Totaal {s}</div>
            </div>
            <div className="font-display text-2xl">€ {(totals[s] || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</div>
          </div>
        ))}
      </div>

      <div className="md:hidden text-[11px] text-neutral-500 mb-2 flex items-center gap-1">
        ← Swipe zijwaarts voor status/betaald-acties →
      </div>

      {(canWrite || canDelete) && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="bulk-toolbar">
          <Button
            size="sm"
            variant="outline"
            onClick={toggleAll}
            data-testid="bulk-select-all"
            className="h-8 text-xs"
          >
            {allSelected
              ? <><CheckSquare className="h-3.5 w-3.5 mr-1.5 text-[color:var(--sage)]" /> Alles gedeselecteerd</>
              : <><Square className="h-3.5 w-3.5 mr-1.5" /> Selecteer alle {rows.length}</>}
          </Button>
          <span className="text-xs text-neutral-500" data-testid="bulk-selected-count">
            {selected.size} geselecteerd
          </span>
          <div className="flex-1" />
          {canWrite && (
            <Button
              size="sm"
              onClick={bulkMarkPaid}
              disabled={!someSelected}
              data-testid="bulk-mark-paid"
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-40"
            >
              <Check className="h-3.5 w-3.5 mr-1" /> Markeer als betaald
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              onClick={bulkDelete}
              disabled={!someSelected}
              data-testid="bulk-delete"
              className="h-8 border-red-300 text-red-700 hover:bg-red-50 text-xs disabled:opacity-40"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Verwijder selectie
            </Button>
          )}
        </div>
      )}

      <DataTable columns={columns} rows={rows} testid="facturen-table" />
      {detailInvoice && (
        <InvoiceDetailDialog
          invoice={detailInvoice}
          canWrite={canWrite}
          onClose={() => setDetailInvoice(null)}
          onChanged={() => { load(); }}
        />
      )}
    </div>
  );
}


function InvoiceDetailDialog({ invoice, canWrite, onClose, onChanged }) {
  const [inv, setInv] = useState(invoice);
  const [addOpen, setAddOpen] = useState(false);
  const [line, setLine] = useState({ kind: "extra_opvang", omschrijving: "", uren: "", uurtarief: "", bedrag: "" });

  const reload = async () => {
    try {
      const { data } = await api.get(`/invoices/${inv.id}`);
      setInv(data);
      onChanged?.();
    } catch (e) { /* ignore */ }
  };

  const addLine = async () => {
    try {
      const payload = { kind: line.kind, omschrijving: line.omschrijving };
      if (line.uren) payload.uren = parseFloat(line.uren);
      if (line.uurtarief) payload.uurtarief = parseFloat(line.uurtarief);
      if (line.bedrag) payload.bedrag = parseFloat(line.bedrag);
      await api.post(`/invoices/${inv.id}/add-line`, payload);
      toast.success("Regel toegevoegd");
      setAddOpen(false);
      setLine({ kind: "extra_opvang", omschrijving: "", uren: "", uurtarief: "", bedrag: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeLine = async (idx) => {
    if (!window.confirm("Regel verwijderen?")) return;
    try {
      await api.delete(`/invoices/${inv.id}/line/${idx}`);
      toast.success("Regel verwijderd");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const importExtraOpvang = async () => {
    try {
      const r = await api.post(`/invoices/${inv.id}/import-extra-opvang`);
      if (r.data.added > 0) toast.success(`${r.data.added} extra opvang-dagen geïmporteerd`);
      else toast.info(r.data.message || "Geen nieuwe extra opvang-dagen gevonden");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const kindStyle = {
    regulier: "bg-neutral-50 border-neutral-200 text-neutral-700",
    extra_opvang: "bg-blue-50 border-blue-200 text-blue-700",
    toeslag: "bg-emerald-50 border-emerald-200 text-emerald-700",
    korting: "bg-amber-50 border-amber-200 text-amber-700",
    credit: "bg-red-50 border-red-200 text-red-700",
  };
  const kindLabel = {
    regulier: "Reguliere opvang",
    extra_opvang: "Extra opvang",
    toeslag: "Toeslag",
    korting: "Korting",
    credit: "Credit-correctie",
  };

  const lines = inv.lines || inv.regels || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Factuur <span className="font-mono text-sm">{inv.invoice_number}</span>
            <Badge variant="outline" className={`border ${STATUS_TONE[inv.status]}`}>{inv.status}</Badge>
          </DialogTitle>
          <DialogDescription>
            Periode {MAANDEN[inv.period_month - 1]} {inv.period_year}
          </DialogDescription>
        </DialogHeader>

        {/* Kop-info voor kinderopvangtoeslag */}
        <div className="grid grid-cols-2 gap-3 text-xs bg-neutral-50 rounded-md p-3 border border-neutral-200" data-testid="invoice-meta">
          <div>
            <div className="text-neutral-500">LRK-nummer</div>
            <div className="font-mono font-medium" data-testid="invoice-lrk">
              {inv.lrk_number || <span className="text-red-500">(niet ingesteld op locatie)</span>}
            </div>
          </div>
          <div>
            <div className="text-neutral-500">Locatie</div>
            <div className="font-medium">{inv.location_name || "—"}</div>
          </div>
          <div>
            <div className="text-neutral-500">Uurtarief</div>
            <div className="font-mono font-medium">€ {(inv.uurtarief ?? 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-neutral-500">Uren deze maand</div>
            <div className="font-mono font-medium">{(inv.uren_per_maand ?? 0).toFixed(4)} uur</div>
          </div>
          <div>
            <div className="text-neutral-500">Formule</div>
            <div className="text-[11px]">
              {inv.opvangdagen_per_week ?? "—"} dg × {inv.uren_per_dag?.toFixed?.(2) ?? "—"} u × {inv.contract_weeks_per_year ?? "—"} wkn ÷ 12
            </div>
          </div>
          <div>
            <div className="text-neutral-500">Deelmaand-factor</div>
            <div className="font-mono">{inv.prorate_factor?.toFixed?.(4) ?? "1.0000"}</div>
          </div>
        </div>

        {/* Regels */}
        <div className="space-y-1.5 mt-3" data-testid="invoice-lines">
          {lines.map((l, idx) => (
            <div key={idx} className={`flex items-center gap-2 p-2 rounded-md border ${kindStyle[l.kind] || "bg-neutral-50 border-neutral-200"}`}>
              <span className="text-[10px] uppercase tracking-widest font-medium min-w-[110px]">
                {kindLabel[l.kind] || l.kind || "regel"}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{l.omschrijving || l.description}</div>
                {(l.uren || l.uurtarief) && (
                  <div className="text-[10px] text-neutral-500 font-mono">
                    {l.uren ? `${(+l.uren).toFixed(2)} u` : ""}
                    {l.uren && l.uurtarief ? " × " : ""}
                    {l.uurtarief ? `€ ${(+l.uurtarief).toFixed(2)}` : ""}
                  </div>
                )}
              </div>
              <span className="font-mono font-medium tabular-nums">
                {l.bedrag < 0 ? "− " : ""}€ {Math.abs(l.bedrag).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {canWrite && inv.status !== "betaald" && l.kind !== "regulier" && (
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                  onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}
                  title="Verwijder regel">
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-neutral-200 mt-2">
            <span className="text-sm font-medium">Totaal te betalen</span>
            <span className="font-mono text-lg font-semibold" data-testid="invoice-total">
              € {inv.total.toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {canWrite && inv.status !== "betaald" && (
          <div className="mt-3 space-y-2">
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={importExtraOpvang}
                      data-testid="import-extra-opvang-btn"
                      title="Zoek presentie-dagen buiten contract in deze maand en voeg toe als extra opvang">
                <Upload className="h-3.5 w-3.5 mr-1" /> Import extra opvang uit presentie
              </Button>
            </div>
            {!addOpen && (
              <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="add-line-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Regel toevoegen
              </Button>
            )}
            {addOpen && (
              <div className="border border-neutral-200 rounded-md p-3 space-y-2 bg-white" data-testid="add-line-form">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={line.kind} onValueChange={v => setLine({ ...line, kind: v })}>
                      <SelectTrigger data-testid="line-kind"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="extra_opvang">Extra opvang</SelectItem>
                        <SelectItem value="toeslag">Toeslag</SelectItem>
                        <SelectItem value="korting">Korting</SelectItem>
                        <SelectItem value="credit">Credit-correctie</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Omschrijving</Label>
                    <Input value={line.omschrijving} onChange={e => setLine({ ...line, omschrijving: e.target.value })}
                           data-testid="line-omschrijving" placeholder="bijv. Extra ma 4 maart" />
                  </div>
                  {line.kind === "extra_opvang" ? (
                    <>
                      <div>
                        <Label className="text-xs">Uren</Label>
                        <Input type="number" step="0.25" value={line.uren}
                               onChange={e => setLine({ ...line, uren: e.target.value })}
                               data-testid="line-uren" />
                      </div>
                      <div>
                        <Label className="text-xs">Uurtarief (€)</Label>
                        <Input type="number" step="0.01" value={line.uurtarief}
                               onChange={e => setLine({ ...line, uurtarief: e.target.value })}
                               data-testid="line-uurtarief" />
                      </div>
                    </>
                  ) : (
                    <div className="col-span-2">
                      <Label className="text-xs">Bedrag (€)</Label>
                      <Input type="number" step="0.01" value={line.bedrag}
                             onChange={e => setLine({ ...line, bedrag: e.target.value })}
                             data-testid="line-bedrag"
                             placeholder={line.kind === "korting" || line.kind === "credit" ? "positief invoeren; wordt automatisch negatief" : ""} />
                    </div>
                  )}
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)}>Annuleren</Button>
                  <Button size="sm" onClick={addLine} style={{ background: "var(--sage)" }} className="text-white" data-testid="line-submit">
                    Toevoegen
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.get(`/kinderen/${inv.kind_id}/jaaropgave/${inv.period_year}/pdf`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url; a.download = `Jaaropgave-${inv.period_year}.pdf`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success(`Jaaropgave ${inv.period_year} gedownload`);
            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Jaaropgave download mislukt"); }
          }} data-testid="download-jaaropgave-btn" title="Download jaaroverzicht voor kinderopvangtoeslag">
            <FileDown className="h-4 w-4 mr-1" /> Jaaropgave {inv.period_year}
          </Button>
          <Button variant="outline" onClick={async () => {
            try {
              const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
              window.URL.revokeObjectURL(url);
            } catch (e) { toast.error("PDF download mislukt"); }
          }} data-testid="download-pdf-btn">
            <FileDown className="h-4 w-4 mr-1" /> PDF downloaden
          </Button>
          <Button onClick={onClose}>Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
